import { Link, Navigate } from "react-router-dom";
import { ShieldAlert, LogOut, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { L, t } from "../lib/translations";

export default function Blocked() {
  const { lang } = useLanguage();
  const { blockedInfo, logout, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020204] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-[#E1306C] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!blockedInfo?.blocked) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#020204] text-white flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-2xl rounded-[32px] border border-red-500/20 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 shadow-[0_24px_90px_rgba(0,0,0,0.5)]">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-[22px] flex items-center justify-center mb-5" style={{ background: "linear-gradient(135deg,rgba(225,48,108,0.2),rgba(247,119,55,0.18))", border: "1px solid rgba(225,48,108,0.24)" }}>
            <ShieldAlert className="w-8 h-8 text-[#ff8fb0]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-3">
            {t("Blocked.account_blocked", lang)}
          </h1>
          <p className="max-w-xl text-sm sm:text-base leading-7" style={{ color: "var(--text-secondary)" }}>
            {t("Blocked.access_to_this_account_is_currently_restricted_t", lang)}
          </p>
        </div>

        <div className="mt-7 rounded-[28px] border border-red-500/15 bg-red-500/[0.06] p-5 sm:p-6">
          <div className="text-xs uppercase tracking-[0.22em] mb-2 text-red-300/90">
            {t("Blocked.block_reason", lang)}
          </div>
          <div className="text-base sm:text-lg font-semibold leading-8 whitespace-pre-wrap">
            {blockedInfo.reason || t("Blocked.no_reason_was_provided", lang)}
          </div>
        </div>

        <div className="mt-5 rounded-[24px] border border-white/10 bg-black/20 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#7aa2ff] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">
                {t("NotFound.need_help", lang)}
              </div>
              <div className="text-sm leading-6" style={{ color: "var(--text-secondary)" }}>
                {t("Blocked.if_you_think_this_is_a_mistake_contact_support_a", lang)}
              </div>
              <a href="mailto:ifollowers.israel@gmail.com" className="inline-flex mt-2 text-sm font-semibold text-[#7aa2ff] hover:text-[#9ab7ff] transition-colors">
                ifollowers.israel@gmail.com
              </a>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-center">
          <Button onClick={logout} className="h-11 px-5 rounded-2xl bg-white/8 border border-white/10 text-white hover:bg-white/12">
            <LogOut className="w-4 h-4 me-2" />
            {t("Blocked.sign_out_and_switch_account", lang)}
          </Button>
          <Button asChild className="h-11 px-5 rounded-2xl btn-brand">
            <Link to="/">
              {t("Blocked.go_to_homepage", lang)}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
